import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../context';
import type { ReactionSummary } from '../types';

export function useReactions(mediaId: string | undefined, prefetched?: ReactionSummary[]) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['reactions', mediaId],
    queryFn: () => apiClient.get<{ reactions: ReactionSummary[] }>(`/api/media/${mediaId}/reactions`),
    enabled: !!mediaId && !prefetched,
  });
}

export function useToggleReaction(mediaId: string | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: (emoji: string) =>
      apiClient.post(`/api/media/${mediaId}/reactions`, { emoji }),
    onMutate: async (emoji) => {
      await queryClient.cancelQueries({ queryKey: ['reactions', mediaId] });
      const prev = queryClient.getQueryData(['reactions', mediaId]);
      queryClient.setQueryData(['reactions', mediaId], (old: { reactions: ReactionSummary[] } | undefined) => {
        const existing = old?.reactions || [];
        const found = existing.find((r) => r.emoji === emoji);
        if (found) {
          return { reactions: existing.map((r) => r.emoji === emoji ? { ...r, count: r.count + 1, userReacted: true } : r) };
        }
        return { reactions: [...existing, { emoji, count: 1, userReacted: true }] };
      });
      return { prev };
    },
    onError: (_err, _emoji, ctx) => queryClient.setQueryData(['reactions', mediaId], ctx?.prev),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['reactions', mediaId] });
      if (mediaId) queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (emoji: string) =>
      apiClient.delete(`/api/media/${mediaId}/reactions/${emoji}`),
    onMutate: async (emoji) => {
      await queryClient.cancelQueries({ queryKey: ['reactions', mediaId] });
      const prev = queryClient.getQueryData(['reactions', mediaId]);
      queryClient.setQueryData(['reactions', mediaId], (old: { reactions: ReactionSummary[] } | undefined) => {
        const existing = old?.reactions || [];
        return {
          reactions: existing
            .map((r) => r.emoji === emoji ? { ...r, count: r.count - 1, userReacted: false } : r)
            .filter((r) => r.count > 0),
        };
      });
      return { prev };
    },
    onError: (_err, _emoji, ctx) => queryClient.setQueryData(['reactions', mediaId], ctx?.prev),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['reactions', mediaId] });
      if (mediaId) queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });

  function toggle(emoji: string, currentlyReacted: boolean) {
    if (currentlyReacted) {
      removeMutation.mutate(emoji);
    } else {
      addMutation.mutate(emoji);
    }
  }

  return { toggle, isAdding: addMutation.isPending, isRemoving: removeMutation.isPending };
}
